import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Button,
  Card,
  CardHeader,
  CardBody,
  Avatar,
  Chip
} from '@heroui/react';
import { 
  PencilIcon,
  ArrowRightOnRectangleIcon,
  CreditCardIcon,
  SunIcon, // Added SunIcon
  MoonIcon // Added MoonIcon
} from '@heroicons/react/24/outline';

function Profile() {
  const [error, setError] = useState('');
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light'); // Initialize theme from localStorage or default
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Apply theme effect
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme); // Save theme preference
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  async function handleLogout() {
    setError('');
    try {
      await logout();
      navigate('/login');
    } catch {
      setError('Failed to log out. Please try again.');
    }
  }

  const handleEditProfile = () => {
    navigate('/update-profile');
  };

  const handleManageSubscription = () => {
    // Placeholder for subscription management, e.g., navigate to a Stripe portal
    alert('Subscription management functionality is not yet implemented.');
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900">
        <p className="text-xl text-gray-700 dark:text-gray-300">Loading user profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900 p-6">
      <div className="container mx-auto max-w-2xl">
        <Card className="shadow-xl hover:shadow-2xl transition-shadow duration-300">
          <CardHeader className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">User Profile</h1>
            <div className="flex items-center gap-2">
              <Button
                isIconOnly
                variant="flat"
                onPress={toggleTheme}
                aria-label="Toggle theme"
              >
                {theme === 'light' ? <MoonIcon className="h-5 w-5" /> : <SunIcon className="h-5 w-5" />}
              </Button>
              <Button
                variant="flat" // Changed variant for better visibility
                color="primary"  // Changed color for better visibility
                onPress={() => navigate('/')}
              >
                Back to Flow
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {error && (
              <Chip color="danger" variant="flat" className="w-full mb-4">
                {error}
              </Chip>
            )}

            <div className="flex flex-col items-center mb-8">
              <Avatar
                src={currentUser.photoURL || undefined} // Pass undefined if photoURL is null/empty to use name
                name={currentUser.displayName || (currentUser.email ? currentUser.email.charAt(0).toUpperCase() : 'U')}
                size="xl"
                className="mb-4 bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-200"
              />
              <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300">
                {currentUser.displayName || currentUser.email || 'User'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{currentUser.email || 'No email provided'}</p>
            </div>

            <div className="space-y-4 mb-8">
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Account Created</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {currentUser.metadata?.creationTime ? new Date(currentUser.metadata.creationTime).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Sign In</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {currentUser.metadata?.lastSignInTime ? new Date(currentUser.metadata.lastSignInTime).toLocaleString() : 'N/A'}
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              <Button
                fullWidth
                color="primary"
                variant="solid"
                startContent={<PencilIcon className="h-5 w-5" />}
                onPress={handleEditProfile}
              >
                Edit Profile
              </Button>
              <Button
                fullWidth
                color="secondary"
                variant="flat"
                startContent={<CreditCardIcon className="h-5 w-5" />}
                onPress={handleManageSubscription}
              >
                Manage Subscription
              </Button>
              <Button
                fullWidth
                color="danger"
                variant="light"
                startContent={<ArrowRightOnRectangleIcon className="h-5 w-5" />}
                onPress={handleLogout}
              >
                Logout
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default Profile;
